import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaArrowLeft } from 'react-icons/fa';
import SharedFilter from '../components/SharedFilter';
import { usePageTitle } from '../hooks/usePageTitle';
import bookingService, { Booking } from '../services/bookingService';
import { hiddenIncomeService, CreateHiddenIncomeData } from '../services/hiddenIncomeService';
import { projectService, Project } from '../services/projectService';
import { unitService } from '../services/unitService';
import '../css/pages/AddIncome.css';

const AddReceivableIncome: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();

  // Projects and wings
  const [projects, setProjects] = useState<Project[]>([]);
  const [wingOptions, setWingOptions] = useState<string[]>([]);

  // Filter states
  const [showFilters, setShowFilters] = useState(true);
  const [projectFilter, setProjectFilter] = useState('');
  const [wingFilter, setWingFilter] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [mobileFilter, setMobileFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    project: '',
    wing: '',
    unit: '',
    customer: '',
    mobile: '',
  });
  const [hasAppliedOnce, setHasAppliedOnce] = useState(false);

  // Booking search states
  const [searchResults, setSearchResults] = useState<Booking[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    incomeType: 'other_work',
    amount: '',
    collectedAmount: '',
    description: '',
    incomeDate: new Date().toISOString().split('T')[0],
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadProjects();
  }, []);

  // Load wing options when project changes
  useEffect(() => {
    const fetchWings = async () => {
      try {
        if (!projectFilter) {
          setWingOptions([]);
          return;
        }
        const { units } = await unitService.getUnits(parseInt(projectFilter), { page: 1, limit: 500 });
        const wings = Array.from(new Set((units || []).map(u => (u.wing || '').trim()).filter(Boolean)));
        setWingOptions(wings.sort());
      } catch (err) {
        console.error('Failed to load wings:', err);
        setWingOptions([]);
      }
    };
    fetchWings();
  }, [projectFilter]);

  const loadProjects = async () => {
    try {
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const applyFilters = async (filters: typeof appliedFilters) => {
    setSearchLoading(true);
    try {
      const result = await bookingService.getBookings({
        status: 'active',
        page: 1,
        limit: 100,
        projectId: filters.project ? parseInt(filters.project) : undefined,
        wing: filters.wing || undefined,
        unitNo: filters.unit || undefined,
        memberName: filters.customer || undefined,
        mobile: filters.mobile || undefined,
      });

      setSearchResults(result.bookings || []);
    } catch (error) {
      console.error('Search error:', error);
      alert('Failed to search bookings');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowPaymentForm(true);
    
    // Get unit ref
    const unitRef = booking.unit.wing
      ? `${booking.unit.wing}-${booking.unit.unitNo}`
      : booking.unit.unitNo;
    
    setPaymentForm({
      incomeType: 'other_work',
      amount: '',
      collectedAmount: '',
      description: `Receivable income for ${unitRef}`,
      incomeDate: new Date().toISOString().split('T')[0],
    });
  };

  const handleBack = () => {
    if (showPaymentForm) {
      setShowPaymentForm(false);
      setSelectedBooking(null);
    } else {
      navigate('/hidden-income');
    }
  };

  const getCustomerName = (booking: Booking): string => {
    const primary = booking.applicants.find(a => a.isPrimary) || booking.applicants[0];
    if (!primary) return 'Unknown Customer';
    return `${primary.firstName} ${primary.surname}`.trim();
  };

  const getUnitRef = (booking: Booking): string => {
    return booking.unit.wing
      ? `${booking.unit.wing}-${booking.unit.unitNo}`
      : booking.unit.unitNo;
  };

  const getContactNumber = (booking: Booking): string => {
    const primary = booking.applicants.find(a => a.isPrimary) || booking.applicants[0];
    return primary?.contactNumber || '';
  };

  const formatCurrency = (amount: number | string): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatDate = (dateString: string | Date): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return '-';
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Allow negative amounts for adjustments/reversals
    if (!paymentForm.amount || isNaN(parseFloat(paymentForm.amount))) {
      errors.amount = 'Please enter a valid amount (negative allowed for adjustments)';
    }

    if (!paymentForm.incomeDate) {
      errors.incomeDate = 'Please select an income date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedBooking) {
      alert('No booking selected');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      const createData: CreateHiddenIncomeData = {
        bookingId: selectedBooking.id.toString(),
        incomeType: paymentForm.incomeType,
        amount: paymentForm.amount,
        collectedAmount: paymentForm.collectedAmount || paymentForm.amount,
        description: paymentForm.description,
        incomeDate: paymentForm.incomeDate,
      };

      await hiddenIncomeService.createHiddenIncome(createData);
      alert('Receivable income created successfully');
      navigate('/hidden-income');
    } catch (error) {
      console.error('Create error:', error);
      alert('Failed to create receivable income');
    }
  };

  return (
    <div className="page-container add-income-page">
      {/* Filter Section - Only when not showing payment form */}
      {!showPaymentForm && (
        <SharedFilter
          show={showFilters}
          projectId={projectFilter}
          unitNumber={unitFilter}
          customerName={customerFilter}
          mobile={mobileFilter}
          onProjectChange={setProjectFilter}
          onUnitNumberChange={setUnitFilter}
          onCustomerNameChange={setCustomerFilter}
          onMobileChange={setMobileFilter}
          projects={projects}
          onApply={() => {
            const next = {
              project: projectFilter,
              wing: wingFilter,
              unit: unitFilter,
              customer: customerFilter,
              mobile: mobileFilter,
            };
            setAppliedFilters(next);
            setHasAppliedOnce(true);
            applyFilters(next);
          }}
          onClear={() => {
            setProjectFilter('');
            setWingFilter('');
            setUnitFilter('');
            setCustomerFilter('');
            setMobileFilter('');
            setAppliedFilters({ project: '', wing: '', unit: '', customer: '', mobile: '' });
            setSearchResults([]);
            setSelectedBooking(null);
            setShowPaymentForm(false);
            setHasAppliedOnce(false);
          }}
        />
      )}

      {/* Search Results */}
      {!showPaymentForm && hasAppliedOnce && searchResults.length > 0 && (
        <div className="search-results">
          <h3>Search Results ({searchResults.length})</h3>
          <div className="content-section">
            <table className="standard-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Customer</th>
                  <th>Unit</th>
                  <th>Project</th>
                  <th>Total Amount</th>
                  <th>Booking Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((booking) => (
                  <tr key={booking.id}>
                    <td>{booking.bookingId}</td>
                    <td style={{ textAlign: 'left' }}>{getCustomerName(booking)}</td>
                    <td>{getUnitRef(booking)}</td>
                    <td style={{ textAlign: 'left' }}>{booking.project?.name || booking.unit?.project?.name || 'N/A'}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(booking.totalAmount)}</td>
                    <td>{formatDate(booking.bookingDate)}</td>
                    <td>
                      <button
                        onClick={() => handleSelectBooking(booking)}
                        className="btn btn-primary btn-sm"
                        style={{ padding: '4px 12px', fontSize: '12px' }}
                      >
                        <FaPlus /> Add Income
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data state */}
      {!showPaymentForm && hasAppliedOnce && !searchLoading && searchResults.length === 0 && (
        <div className="search-results">
          <h3>Search Results</h3>
          <div className="content-section" style={{ padding: '16px', fontSize: '14px', color: '#6b7280' }}>
            No data
          </div>
        </div>
      )}

      {/* Payment Form Section */}
      {showPaymentForm && selectedBooking && (
        <div className="payment-section">
          <div className="selected-booking" style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <h3>Selected Booking</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px', marginTop: '12px' }}>
              <div>
                <strong>Customer:</strong> {getCustomerName(selectedBooking)}
              </div>
              <div>
                <strong>Unit:</strong> {getUnitRef(selectedBooking)}
              </div>
              <div>
                <strong>Project:</strong> {selectedBooking.project?.name || selectedBooking.unit?.project?.name || 'N/A'}
              </div>
              <div>
                <strong>Booking ID:</strong> {selectedBooking.bookingId}
              </div>
              <div>
                <strong>Total Amount:</strong> {formatCurrency(selectedBooking.totalAmount)}
              </div>
              <div>
                <strong>Booking Date:</strong> {formatDate(selectedBooking.bookingDate)}
              </div>
              {getContactNumber(selectedBooking) && (
                <div>
                  <strong>Contact:</strong> {getContactNumber(selectedBooking)}
                </div>
              )}
            </div>
          </div>

          <div className="content-section">
            <h3>Income Details</h3>
            <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label>Income Type *</label>
                  <select
                    value={paymentForm.incomeType}
                    onChange={(e) => setPaymentForm({ ...paymentForm, incomeType: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '13px',
                    }}
                  >
                    <option value="receivables">Receivables</option>
                    <option value="charges">Charges</option>
                    <option value="terrace">Terrace</option>
                    <option value="other_work">Other Work</option>
                    <option value="banakhat">Banakhat</option>
                    <option value="adani_gas">Adani Gas</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPaymentForm({ ...paymentForm, amount: value, collectedAmount: value });
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="Enter amount"
                    step="0.01"
                    required
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: `1px solid ${formErrors.amount ? '#ef4444' : '#e5e7eb'}`,
                      borderRadius: '6px',
                      fontSize: '13px',
                    }}
                  />
                  {formErrors.amount && <span style={{ color: '#ef4444', fontSize: '12px' }}>{formErrors.amount}</span>}
                </div>

                <div className="form-group">
                  <label>Income Date *</label>
                  <input
                    type="date"
                    value={paymentForm.incomeDate}
                    onChange={(e) => setPaymentForm({ ...paymentForm, incomeDate: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: `1px solid ${formErrors.incomeDate ? '#ef4444' : '#e5e7eb'}`,
                      borderRadius: '6px',
                      fontSize: '13px',
                    }}
                  />
                  {formErrors.incomeDate && <span style={{ color: '#ef4444', fontSize: '12px' }}>{formErrors.incomeDate}</span>}
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={paymentForm.description}
                    onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                    placeholder="Optional description"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '13px',
                      resize: 'vertical',
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={handleBack}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <FaPlus /> Create Income
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddReceivableIncome;
